import React, { useEffect, useMemo, useState } from 'react';
import { Button, Descriptions, Empty, Image, Skeleton, Timeline } from 'antd';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SectionCard from './components/SectionCard';
import { ActivityStatusTag } from './components/ActivityStatusTag';
import RuleTextView from './components/RuleTextView';
import RuleTextPreviewModal, { RuleTextPreviewSource } from './components/RuleTextPreviewModal';
import { useRuleTemplate } from './hooks/useRuleTemplate';
import { useSiteOptions } from './hooks/useSiteOptions';
import { GET_DRIVER_ACTIVITY_ADMIN_DETAIL, GetDriverActivityAdminDetailResponse, GetDriverActivityAdminDetailVariables } from './graphql/queries';
import { DriverActivityAdminStatus, DriverActivityRule } from './graphql/types';
import { formatActivityLog } from './utils/submitHelpers';
import { formatLaDateTime } from './utils/activityFormUtils';
import { listPathFromState } from './utils/listQuery';
import { showError } from '../../../utils/exceptionUtils';

const LABEL_STYLE: React.CSSProperties = { width: 150, justifyContent: 'flex-end', color: '#8C8C8C', paddingRight: 16 };
const CONTENT_STYLE: React.CSSProperties = { color: '#1F1F1F' };

/**
 * 活动详情（原型页面 3）：内容同编辑页四模块，全部只读；操作日志倒序时间线；右上「返回列表」。
 */
const DriverActivityDetailPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    // 从列表进来时带着列表的筛选，返回时原样带回
    const listPath = listPathFromState(location.state);
    const { id } = useParams<{ id: string }>();
    const activityId = Number(id);
    const validId = Number.isInteger(activityId) && activityId > 0;
    const { template } = useRuleTemplate();
    const sites = useSiteOptions();
    const [previewOpen, setPreviewOpen] = useState(false);

    const { data, loading, error } = useQuery<GetDriverActivityAdminDetailResponse, GetDriverActivityAdminDetailVariables>(GET_DRIVER_ACTIVITY_ADMIN_DETAIL, {
        variables: { id: activityId },
        skip: !validId,
        fetchPolicy: 'network-only',
    });
    const detail = data?.getDriverActivityAdminDetail;
    // 拉不到详情（已删除 / 不存在）时提示一次，页面落到空态而不是一直骨架屏
    useEffect(() => {
        if (error) showError(error);
    }, [error]);

    const previewSource = useMemo<RuleTextPreviewSource | null>(() => {
        if (!detail) return null;
        return {
            startDate: detail.startDate ?? null,
            endDate: detail.endDate ?? null,
            maxRewardAmount: detail.maxRewardAmount ?? null,
            rules: detail.rules.map((rule) => ({
                scopeId: rule.scopeId ?? null,
                hubIds: rule.hubIds,
                warehouseIds: rule.warehouseIds,
                levels: rule.levels.map((level) => ({
                    level: level.level,
                    dailyDeliverQty: level.dailyDeliverQty,
                    targetAttendDays: level.targetAttendDays,
                    rewardAmount: level.rewardAmount,
                })),
            })),
            ruleText: detail.ruleText.map((item) => ({
                language: item.language,
                sections: item.sections.map((s) => ({ key: s.key, content: s.content })),
            })),
        };
    }, [detail]);

    const siteName = (siteId: number) => sites.siteNameMap[siteId] ?? String(siteId);

    /** 「LAX · 全部 WHS；EWR · EWR_JFK、EWR_NJ」：整 Hub 在前，单独选的仓按所属 Hub 分组 */
    const describeScope = (rule: DriverActivityRule): string => {
        const parts: string[] = rule.hubIds.map((hubId) => `${siteName(hubId)} · ${t('driverOps.activity.detail.allWhs')}`);
        const byHub = new Map<number, number[]>();
        rule.warehouseIds.forEach((whsId) => {
            const hubId = sites.whsHubMap[whsId] ?? 0;
            if (!byHub.has(hubId)) byHub.set(hubId, []);
            byHub.get(hubId)!.push(whsId);
        });
        byHub.forEach((whsIds, hubId) => {
            const hubLabel = hubId ? siteName(hubId) : t('driverOps.activity.form.otherHubGroup');
            parts.push(`${hubLabel} · ${whsIds.map(siteName).join(String(t('driverOps.activity.form.listSeparator')))}`);
        });
        return parts.join(String(t('driverOps.activity.form.groupSeparator')));
    };

    if (!validId) {
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
        <div>
            <div className="text-[13px] text-[#8C8C8C] mb-2">
                <span className="cursor-pointer" onClick={() => navigate(listPath)}>{t('driverOps.activity.title')}</span>
                {' / '}
                <span className="text-[#595959]">{t('driverOps.activity.detail.title')}</span>
            </div>
            <div className="flex justify-between items-center">
                <h2 className="flex items-center gap-3 text-[26px] font-bold m-0">
                    {detail?.name ?? t('driverOps.activity.detail.title')}
                    {detail && <ActivityStatusTag status={detail.status} />}
                </h2>
                <Button onClick={() => navigate(listPath)}>{t('driverOps.activity.action.backToList')}</Button>
            </div>
<div className="mb-[22px]" />

            {error || (!loading && !detail) ? (
                <div className="bg-white rounded-[10px] p-6">
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
            ) : loading || !detail ? (
                <div className="bg-white rounded-[10px] p-6">
                    <Skeleton active paragraph={{ rows: 10 }} />
                </div>
            ) : (
                <>
                    <SectionCard
                        title={t('driverOps.activity.form.basicInfo')}
                        // 时间口径标签放卡头，不塞在「活动时间」格里撑高行（两列表格一行取两边较高的格，右边会空出一截）
                        extra={
                            <span className="inline-block bg-[#F3EEFE] text-[#722ed1] rounded-[6px] px-[10px] py-1 text-[12.5px] font-semibold">
                                {t('driverOps.activity.form.timeZoneLine')}
                            </span>
                        }
                    >
                        {/* 左右两列各自一张单列表格：右列条数可以多于左列（下线时间、下线原因各占一行），标签都落在标签列上对齐 */}
                        <div className="grid grid-cols-2 gap-x-7">
                            <Descriptions column={1} colon={false} labelStyle={LABEL_STYLE} contentStyle={CONTENT_STYLE}>
                                <Descriptions.Item label={t('driverOps.activity.form.name')}>{detail.name}</Descriptions.Item>
                                <Descriptions.Item label={t('driverOps.activity.form.time')}>
                                    {detail.startDate ?? '--'} ~ {detail.endDate ?? '--'}
                                    {t('driverOps.activity.form.parenthesized', { text: t('driverOps.activity.detail.byWeek', { count: detail.weekCount }) })}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('driverOps.activity.form.banner')}>
                                    {detail.bannerUrl ? (
                                        // 缩略图点开看 750×300 原图（antd Image 自带预览层，支持缩放）
                                        <Image
                                            src={detail.bannerUrl}
                                            alt="banner"
                                            width={150}
                                            height={60}
                                            className="rounded-md object-cover cursor-zoom-in"
                                            preview={{ mask: t('driverOps.activity.detail.bannerPreview') }}
                                        />
                                    ) : (
                                        '--'
                                    )}
                                </Descriptions.Item>
                            </Descriptions>
                            <Descriptions column={1} colon={false} labelStyle={LABEL_STYLE} contentStyle={CONTENT_STYLE}>
                                <Descriptions.Item label={t('driverOps.activity.form.type')}>
                                    {t(`driverOps.activity.type.${detail.type}`, { defaultValue: detail.type })}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('driverOps.activity.form.signUpDeadline')}>
                                    {detail.needSignUp ? detail.signUpDeadline ?? '--' : t('driverOps.activity.onlineConfirm.noSignUp')}
                                </Descriptions.Item>
                                {detail.status === DriverActivityAdminStatus.OFFLINE && (
                                    <Descriptions.Item label={t('driverOps.activity.detail.offlineTime')}>
                                        <span className="text-[#F0453A]">{formatLaDateTime(detail.offlineTime)}</span>
                                    </Descriptions.Item>
                                )}
                                {detail.status === DriverActivityAdminStatus.OFFLINE && (
                                    <Descriptions.Item label={t('driverOps.activity.detail.offlineReason')}>
                                        <span className="text-[#F0453A]">{detail.offlineReason || '--'}</span>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </div>
                    </SectionCard>

                    <div className="grid grid-cols-2 gap-x-7">
                        <SectionCard title={t('driverOps.activity.form.participation')}>
                            <Descriptions column={1} colon={false} labelStyle={LABEL_STYLE} contentStyle={CONTENT_STYLE}>
                                <Descriptions.Item label={t('driverOps.activity.form.needSignUp')}>{detail.needSignUp ? t('common.yes') : t('common.no')}</Descriptions.Item>
                                <Descriptions.Item label={t('driverOps.activity.form.needRealName')}>{detail.needRealName ? t('common.yes') : t('common.no')}</Descriptions.Item>
                            </Descriptions>
                        </SectionCard>
                        <SectionCard title={t('driverOps.activity.form.ruleCard')}>
                            <div className="text-[14px] leading-[1.9] text-[#1F1F1F]">
                                {detail.rules.map((rule, index) => (
                                    <div key={rule.scopeId ?? index}>
                                        <b className="mr-[14px]">{t('driverOps.activity.form.ruleNo', { no: index + 1 })}</b>
                                        {describeScope(rule)}
                                        {[...rule.levels]
                                            .sort((a, b) => a.level - b.level)
                                            .map((level) => (
                                                <div key={level.level} className="pl-[14px]">
                                                    {t('driverOps.activity.detail.levelLine', {
                                                        level: level.level,
                                                        qty: level.dailyDeliverQty,
                                                        days: level.targetAttendDays,
                                                        amount: level.rewardAmount,
                                                    })}
                                                </div>
                                            ))}
                                    </div>
                                ))}
                                <div>
                                    {t('driverOps.activity.detail.maxRewardLine', { amount: detail.maxRewardAmount ?? '--' })}
                                </div>
                            </div>
                        </SectionCard>
                    </div>

                    <div className="grid grid-cols-2 gap-x-7">
                        <SectionCard title={t('driverOps.activity.form.ruleTextCard')}>
                            <RuleTextView
                                ruleText={detail.ruleText}
                                template={template}
                                extra={
                                    <Button size="small" onClick={() => setPreviewOpen(true)}>
                                        {t('driverOps.activity.preview.button')}
                                    </Button>
                                }
                            />
                        </SectionCard>
                        <SectionCard title={t('driverOps.activity.detail.logs')}>
                            {detail.logs.length === 0 ? (
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <Timeline
                                    items={[...detail.logs]
                                        .sort((a, b) => b.id - a.id)
                                        .map((log) => ({
                                            color: '#722ed1',
                                            children: (
                                                <div className="text-[13.5px]">
                                                    {formatActivityLog(log, t, 'full')}
                                                </div>
                                            ),
                                        }))}
                                />
                            )}
                        </SectionCard>
                    </div>
                </>
            )}

            {previewSource && (
                <RuleTextPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} source={previewSource} languages={template.languages} sections={template.sections} />
            )}
        </div>
    );
};

export default DriverActivityDetailPage;
